import axios from 'axios';

import { clearUserIsActiveWatcher } from '../hooks/useUserIsActiveWatcher';
import { AuthAPI } from '../services/rtk/AuthAPI';
import { SettingsAPI } from '../services/rtk/SettingsAPI';
import { AuthData, AuthDataTokenType } from '../types/Api';
import { dayjs } from './DateTime';
import { generateUrlWithParameters } from './FormattingHelpers';
import { authChannel } from './messages';

// Checks if there is a saved token and it's still valid
export const userIsAuthenticated = (token: string | null): boolean => {
    return !!token && !isTokenExpired(token);
};

const isTokenExpired = (token: string): boolean => {
    try {
        const decoded = parseJwtToken(token);
        return decoded.exp < Date.now() / 1000;
    } catch (err) {
        return false;
    }
};

export const parseJwtToken = (token: string): any => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
        atob(base64)
            .split('')
            .map((c) => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join('')
    );

    return JSON.parse(jsonPayload);
};

// Retrieves the user token from storage
export const getToken = (tokenType: AuthDataTokenType = 'accessToken'): string | null => {
    const authData = JSON.parse(localStorage.getItem('authData') as string);
    if (authData && authData[tokenType]) {
        return authData[tokenType];
    }
    return null;
};

// Saves auth data to storage
export const setAuthData = (data: AuthData): void => {
    const authData = {
        ...data,
        expiresAt: dayjs().unix() + data.expiresIn
    };
    localStorage.setItem('authData', JSON.stringify(authData));
};

export const clearAppDataAndRouteToLogin = () => {
    clearUserIsActiveWatcher();
    localStorage.clear();
    AuthAPI.util.resetApiState();
    SettingsAPI.util.resetApiState();
    routeToLogin();
};

export const logout = (): void => {
    authChannel.postMessage({ type: 'logout' });
    clearAppDataAndRouteToLogin();
};

export const routeToLogin = (): void => {
    if (import.meta.env.VITE_SCANNING_COGNITO_LOGIN_PATH) {
        const url = generateUrlWithParameters(import.meta.env.VITE_SCANNING_COGNITO_LOGIN_PATH, {
            client_id: import.meta.env.VITE_SCANNING_COGNITO_CLIENT_ID ?? '',
            response_type: 'code',
            redirect_uri: import.meta.env.VITE_SCANNING_COGNITO_REDIRECT_PATH ?? ''
        });
        window.location.href = url.href;
    }
};

export const isAxiosErrorWithStatus = (err: unknown, code: number) =>
    axios.isAxiosError(err) && err.response?.status === code;
